// Globals
import type { FC, KeyboardEvent } from 'react';

// Types
import type {
  SharedAriaLinkProps,
  KeyDownData,
} from './shared-aria-link.types';
import type { LinksFunction } from '@remix-run/node';

// Misc
import clsx from 'clsx';
import { useNavigate } from '@remix-run/react';

import styles from './shared-aria-link.css?url';

export const links: LinksFunction = () => [{ rel: 'stylesheet', href: styles }];

// Util
function reduceKeyDownHandlers(
  handlers: KeyDownData[],
  event: KeyboardEvent<HTMLDivElement>,
) {
  return handlers.reduce((array: KeyDownData[], data: KeyDownData) => {
    // Add event from onKeyDown to data
    data.event = event;
    array.push(data);
    return array;
  }, []);
}

// Component
export const SharedAriaLink: FC<SharedAriaLinkProps> = ({
  children,
  className = '',
  display = '',
  href = '',
  // Default to empty intent so if non-button styles needed then there will be no default button styles to revert
  intent = 'none',
  isDisabled = false,
  isFullWidth = false,
  keyDownData = [],
  onClick,
  path = '',
  ...rest
}) => {
  // PropType error
  if (href && path) {
    throw new Error('AriaLink cannot use both href and path, choose one');
  }

  // Hooks
  const navigate = useNavigate();

  // Handlers
  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const eventData = reduceKeyDownHandlers(keyDownData, event);
    const handlers = [
      ...eventData,
      // Always fire handleOnClick on 'Enter'
      {
        event,
        key: 'Enter',
        handler: handleOnClick,
      },
    ];

    handlers.forEach(({ event, key, handler }) => {
      if (event.key === key) {
        event.preventDefault();
        if (!handler || isDisabled) {
          return;
        }
        handler(event);
      }
    });
  };

  const handleOnClick = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLDivElement>,
  ) => {
    // Prevent click event from bubbling
    event.stopPropagation();

    if (isDisabled) return;

    // Optional onClick
    if (onClick) {
      onClick(event);
    }

    // Absolute path
    if (href) {
      window.open(href, '_blank');
      // Relative path
    } else if (path) {
      navigate(path);
    }
  };

  // Vars
  const classes = clsx(
    'shared-aria-link',
    {
      'shared-aria-link--button': intent !== 'none',
      'shared-aria-link--primary': intent === 'primary',
      'shared-aria-link--secondary': intent === 'secondary',
      'shared-aria-link--display-inline': display === 'inline',
      'shared-aria-link--disabled': isDisabled,
      'shared-aria-link--full-width': isFullWidth,
    },
    className,
  );

  // Render
  return (
    <div
      className={classes}
      onClick={handleOnClick}
      onKeyDown={handleOnKeyDown}
      role="link"
      tabIndex={0}
      {...rest}
    >
      {children}
    </div>
  );
};
